import React, { useState } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useLanguage } from '../../context/LanguageContext';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';
import { useAuth } from '../../context/AuthContext';
import { Trash2, Shield, ShieldOff, AlertTriangle, X, UserPlus, Eye, EyeOff, UserCog, RotateCcw } from 'lucide-react';
import axios from 'axios';
import logger from '../../utils/logger';

import { API_URL } from '../../utils/apiConfig';
import ImpersonationReasonModal from './ImpersonationReasonModal';

const AdminUsersTab = ({ users, currentUserId, onRefetch }) => {
  const { isDark } = useTheme();
  const { t, language } = useLanguage();
  const { showError } = useToast();
  const { confirm } = useConfirm();
  const { impersonate } = useAuth();
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [impersonateTarget, setImpersonateTarget] = useState(null);
  const [loading, setLoading] = useState(false);

  // Create user form state
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [createForm, setCreateForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'user'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [createError, setCreateError] = useState('');

  const superAdminCount = users.filter(u => u.cs_admin && u.is_active !== false).length;

  const handleToggleCsAdmin = async (userId, currentValue) => {
    const next = !currentValue;

    if (userId === currentUserId && !next) {
      if (!await confirm({ message: t('confirmations.removeAdminSelf'), variant: 'danger' })) return;
    }

    setLoading(true);
    try {
      await axios.put(`${API_URL}/api/admin/users/${userId}/cs-admin`, { cs_admin: next });
      onRefetch();
    } catch (error) {
      logger.error('Failed to update cs_admin flag:', error);
      showError(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    setLoading(true);
    try {
      await axios.delete(`${API_URL}/api/admin/users/${userId}`);
      setDeleteConfirm(null);
      onRefetch();
    } catch (error) {
      logger.error('Failed to delete user:', error);
      showError(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  const handleCancelDeletion = async (userId) => {
    if (!await confirm({ message: t('admin.users.cancelDeletionConfirm'), variant: 'warning' })) return;

    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/admin/users/${userId}/cancel-deletion`);
      onRefetch();
    } catch (error) {
      logger.error('Failed to cancel deletion:', error);
      showError(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  // Lot 3 : ouvre le modal motif au lieu du confirm oui/non. Le submit
  // du modal (motif >= 10 chars) déclenche handleImpersonateConfirmed.
  const handleImpersonate = (target) => {
    setImpersonateTarget(target);
  };

  const handleImpersonateConfirmed = async (reason) => {
    const target = impersonateTarget;
    if (!target) return;
    setImpersonateTarget(null);
    setLoading(true);
    try {
      await impersonate(target.id, reason);
      // Full reload + history replace: soft routing leaves stale state in
      // FiscalYearContext / useDataEntry that still belongs to the cs_admin's
      // session (H.2.1). RF3 : replace UNIQUEMENT après le 200 de l'API.
      window.location.replace('/');
    } catch (error) {
      logger.error('Failed to impersonate user:', error);
      showError(error.response?.data?.detail || t('impersonation.error'));
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setCreateError('');
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/admin/users`, createForm);
      setShowCreateForm(false);
      setCreateForm({ email: '', password: '', name: '', role: 'user' });
      onRefetch();
    } catch (error) {
      logger.error('Failed to create user:', error);
      setCreateError(error.response?.data?.detail || t('errors.generic'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {/* Create User Button */}
      <div className="mb-4">
        <button
          onClick={() => setShowCreateForm(true)}
          data-testid="create-user-btn"
          className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl font-medium transition-colors ${
            isDark 
              ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' 
              : 'bg-green-500 text-white hover:bg-green-600'
          }`}
        >
          <UserPlus className="w-5 h-5" />
          {t('admin.users.createUser')}
        </button>
      </div>

      {/* Users Table */}
      <div className={`rounded-xl border overflow-hidden ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-white border-gray-200'}`}>
        <table className="w-full">
          <thead>
            <tr className={isDark ? 'bg-slate-700' : 'bg-gray-50'}>
              <th className="text-left px-4 py-3 font-medium">{t('admin.users.email')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('admin.users.name')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('admin.users.platformStatus')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('admin.users.organization')}</th>
              <th className="text-left px-4 py-3 font-medium">{t('admin.users.createdAt')}</th>
              <th className="text-right px-4 py-3 font-medium">{t('common.actions')}</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const isSelf = u.id === currentUserId;
              const isLastSuperAdminOnRow = u.cs_admin && superAdminCount <= 1;
              const cantToggleSelfDown = isSelf && isLastSuperAdminOnRow;
              const isPendingDeletion = !!u.deletion_scheduled_at && !u.deleted_at;
              const isHardDeleted = !!u.deleted_at;
              const scheduledDate = isPendingDeletion
                ? new Date(u.deletion_scheduled_at).toLocaleDateString('fr-CH')
                : '';
              return (
              <tr
                key={u.id}
                data-testid={`user-row-${u.id}`}
                className={`border-t ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'} ${
                  isPendingDeletion || isHardDeleted ? 'opacity-60' : ''
                }`}
              >
                <td className="px-4 py-3">{u.email}</td>
                <td className="px-4 py-3">{u.name || '-'}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    u.cs_admin
                      ? 'bg-amber-500/20 text-amber-500'
                      : isDark ? 'bg-slate-600 text-slate-300' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {u.cs_admin ? t('admin.users.superAdmin') : t('admin.users.regularUser')}
                  </span>
                  {isSelf && (
                    <span className={`ml-2 text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>({t('auth.you')})</span>
                  )}
                  {isPendingDeletion && (
                    <span
                      data-testid={`pending-deletion-badge-${u.id}`}
                      className="ml-2 px-2 py-1 rounded text-xs font-medium bg-red-500/20 text-red-500"
                    >
                      {t('admin.users.pendingDeletionBadge').replace('{date}', scheduledDate)}
                    </span>
                  )}
                  {isHardDeleted && (
                    <span
                      data-testid={`hard-deleted-badge-${u.id}`}
                      className={`ml-2 px-2 py-1 rounded text-xs font-medium ${isDark ? 'bg-slate-600 text-slate-400' : 'bg-gray-200 text-gray-500'}`}
                    >
                      {t('admin.users.hardDeletedBadge')}
                    </span>
                  )}
                </td>
                <td className={`px-4 py-3 text-sm ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {u.company_name || '—'}
                </td>
                <td className="px-4 py-3 text-sm">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString('fr-CH') : '-'}
                </td>
                <td className="px-4 py-3 text-right space-x-2">
                  {/* Cancel Deletion Button — visible only for users pending deletion */}
                  {isPendingDeletion && (
                    <button
                      data-testid={`cancel-deletion-${u.id}`}
                      onClick={() => handleCancelDeletion(u.id)}
                      disabled={loading}
                      title={t('admin.users.cancelDeletion')}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-green-500/10 text-green-500 hover:bg-green-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <RotateCcw className="w-4 h-4" />
                      {t('admin.users.cancelDeletion')}
                    </button>
                  )}

                  {/* Toggle cs_admin Button */}
                  <button
                    data-testid={`toggle-cs-admin-${u.id}`}
                    onClick={() => handleToggleCsAdmin(u.id, u.cs_admin)}
                    disabled={loading || cantToggleSelfDown || isPendingDeletion || isHardDeleted}
                    title={
                      isPendingDeletion || isHardDeleted
                        ? t('admin.users.actionUnavailablePendingDeletion')
                        : cantToggleSelfDown
                        ? t('admin.users.lastSuperAdminTooltip')
                        : ''
                    }
                    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm transition-colors ${
                      u.cs_admin
                        ? 'bg-orange-500/10 text-orange-500 hover:bg-orange-500/20'
                        : 'bg-green-500/10 text-green-500 hover:bg-green-500/20'
                    } disabled:opacity-50 disabled:cursor-not-allowed`}
                  >
                    {u.cs_admin ? (
                      <>
                        <ShieldOff className="w-4 h-4" />
                        {t('admin.users.removeSuperAdmin')}
                      </>
                    ) : (
                      <>
                        <Shield className="w-4 h-4" />
                        {t('admin.users.promoteSuperAdmin')}
                      </>
                    )}
                  </button>

                  {/* Impersonate Button - hidden for self and other cs_admin (Phase H.2) */}
                  {!isSelf && !u.cs_admin && (
                    <button
                      data-testid={`impersonate-${u.id}`}
                      onClick={() => handleImpersonate(u)}
                      disabled={loading || u.is_active === false}
                      title={u.is_active === false ? t('admin.users.impersonateUserDisabled') : ''}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-blue-500/10 text-blue-500 hover:bg-blue-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <UserCog className="w-4 h-4" />
                      {t('admin.users.impersonate')}
                    </button>
                  )}

                  {/* Delete Button - only show for non-current user */}
                  {!isSelf && (
                    <button
                      data-testid={`delete-user-${u.id}`}
                      onClick={() => setDeleteConfirm(u)}
                      disabled={loading || isLastSuperAdminOnRow || isPendingDeletion || isHardDeleted}
                      title={
                        isPendingDeletion || isHardDeleted
                          ? t('admin.users.deleteDisabledPendingDeletion')
                          : isLastSuperAdminOnRow
                          ? t('admin.users.cantDeleteLastSuperAdmin')
                          : ''
                      }
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded text-sm bg-red-500/10 text-red-500 hover:bg-red-500/20 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Trash2 className="w-4 h-4" />
                      {t('admin.users.delete')}
                    </button>
                  )}
                </td>
              </tr>
            );
            })}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="flex items-center justify-between mb-6">
              <h3 className={`text-xl font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                {t('admin.users.createUser')}
              </h3>
              <button
                onClick={() => {
                  setShowCreateForm(false);
                  setCreateError('');
                }}
                className={`p-2 rounded-lg hover:bg-slate-700/50 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleCreateUser} className="space-y-4">
              {/* Email */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={createForm.email}
                  onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
                  data-testid="create-user-email"
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDark 
                      ? 'bg-slate-700 border-slate-600 text-white focus:border-green-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:border-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-green-500/20`}
                  placeholder="utilisateur@example.com"
                />
              </div>

              {/* Name */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('admin.users.name')}
                </label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })}
                  data-testid="create-user-name"
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDark 
                      ? 'bg-slate-700 border-slate-600 text-white focus:border-green-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:border-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-green-500/20`}
                  placeholder={t('admin.users.namePlaceholder')}
                />
              </div>

              {/* Password */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('admin.users.password')} *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={6}
                    value={createForm.password}
                    onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
                    data-testid="create-user-password"
                    className={`w-full px-4 py-2.5 pr-12 rounded-xl border transition-colors ${
                      isDark 
                        ? 'bg-slate-700 border-slate-600 text-white focus:border-green-500' 
                        : 'bg-white border-gray-300 text-gray-900 focus:border-green-500'
                    } focus:outline-none focus:ring-2 focus:ring-green-500/20`}
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded ${isDark ? 'text-slate-400 hover:text-slate-300' : 'text-gray-400 hover:text-gray-600'}`}
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>
                  {t('admin.users.minChars')}
                </p>
              </div>

              {/* Role */}
              <div>
                <label className={`block text-sm font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                  {t('admin.users.role')}
                </label>
                <select
                  value={createForm.role}
                  onChange={(e) => setCreateForm({ ...createForm, role: e.target.value })}
                  data-testid="create-user-role"
                  className={`w-full px-4 py-2.5 rounded-xl border transition-colors ${
                    isDark 
                      ? 'bg-slate-700 border-slate-600 text-white focus:border-green-500' 
                      : 'bg-white border-gray-300 text-gray-900 focus:border-green-500'
                  } focus:outline-none focus:ring-2 focus:ring-green-500/20`}
                >
                  <option value="user">{t('auth.user')}</option>
                  <option value="admin">{t('auth.admin')}</option>
                </select>
              </div>

              {/* Error message */}
              {createError && (
                <div className="p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                  {createError}
                </div>
              )}

              {/* Buttons */}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateForm(false);
                    setCreateError('');
                  }}
                  className={`flex-1 px-4 py-2.5 rounded-xl font-medium transition-colors ${
                    isDark 
                      ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="submit-create-user"
                  className="flex-1 px-4 py-2.5 rounded-xl font-medium bg-green-500 text-white hover:bg-green-600 transition-colors disabled:opacity-50"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      {t('admin.users.creating')}
                    </span>
                  ) : (
                    t('common.create')
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className={`w-full max-w-md rounded-2xl shadow-2xl p-6 ${isDark ? 'bg-slate-800' : 'bg-white'}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 rounded-full bg-red-500/10">
                <AlertTriangle className="w-6 h-6 text-red-500" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                  {t('admin.users.deleteConfirmTitle')}
                </h3>
              </div>
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`ml-auto p-2 rounded-lg hover:bg-slate-700/50 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className={`mb-6 ${isDark ? 'text-slate-300' : 'text-gray-600'}`}>
              {t('admin.users.deleteConfirmMsg').replace('{email}', deleteConfirm.email)}
            </p>
            
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteConfirm(null)}
                className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                  isDark 
                    ? 'bg-slate-700 text-slate-300 hover:bg-slate-600' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={() => handleDeleteUser(deleteConfirm.id)}
                disabled={loading}
                data-testid="confirm-delete-user"
                className="px-4 py-2 rounded-lg font-medium bg-red-500 text-white hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                    {t('admin.users.deleting')}
                  </span>
                ) : (
                  t('admin.users.deletePermanently')
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Lot 3 impersonation-mail — modal motif obligatoire */}
      <ImpersonationReasonModal
        isOpen={!!impersonateTarget}
        onClose={() => setImpersonateTarget(null)}
        onConfirm={handleImpersonateConfirmed}
        targetName={impersonateTarget?.name || impersonateTarget?.email || ''}
        isDark={isDark}
        t={t}
      />
    </>
  );
};

export default AdminUsersTab;
